
import { useState } from 'react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    clearanceLevel: '',
    message: '',
    urgency: 'standard'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/submit/contact-form-shadow-security', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          clearanceLevel: '',
          message: '',
          urgency: 'standard'
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    'Executive Security',
    'Tactical Training',
    'Combat Medicine (TCCC)',
    'Reconnaissance & Tracking',
    'NVG Operations',
    'Force Protection',
    'AI Surveillance Systems',
    'Smart Solar Lights',
    'Facial Recognition',
    'Gunshot Detection',
    'Drone Detection',
    'Custom Security Solutions'
  ];

  const clearanceLevels = [
    'Unclassified',
    'Confidential',
    'Secret',
    'Top Secret',
    'TS/SCI',
    'Other/Specify in Message'
  ];

  return (
    <div className="min-h-screen bg-black text-white pt-16">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">
            Secure <span className="text-yellow-500">Contact</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Reach out to SHADOW for confidential consultation on your security requirements
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold mb-8 text-white">Get In Touch</h2>
              
              <div className="space-y-8">
                <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 text-yellow-500">Emergency Contact</h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <i className="ri-phone-line text-yellow-500 mr-4 text-xl"></i>
                      <div>
                        <div className="text-white font-medium">24/7 Emergency Line</div>
                        <div className="text-gray-400">+1 (555) 911-SHADOW</div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-mail-line text-yellow-500 mr-4 text-xl"></i>
                      <div>
                        <div className="text-white font-medium">Emergency Email</div>
                        <div className="text-gray-400">emergency@shadowsecurity.com</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 text-yellow-500">Business Inquiries</h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <i className="ri-phone-line text-yellow-500 mr-4 text-xl"></i>
                      <div>
                        <div className="text-white font-medium">Main Office</div>
                        <div className="text-gray-400">+1 (555) 123-4567</div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-mail-line text-yellow-500 mr-4 text-xl"></i>
                      <div>
                        <div className="text-white font-medium">General Inquiries</div>
                        <div className="text-gray-400">info@shadowsecurity.com</div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <i className="ri-map-pin-line text-yellow-500 mr-4 text-xl mt-1"></i>
                      <div>
                        <div className="text-white font-medium">Headquarters</div>
                        <div className="text-gray-400">
                          Classified Location<br />
                          Secure Government Facility<br />
                          United States
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 text-yellow-500">Security Notice</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    All communications are monitored and encrypted. Sensitive information should only be discussed through secure channels. 
                    For classified matters, please indicate your clearance level in the form below.
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-xl p-8">
                <h2 className="text-2xl font-bold mb-6 text-white">Request Consultation</h2>
                
                <form id="contact-form-shadow-security" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-colors text-sm"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-colors text-sm"
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-colors text-sm"
                        placeholder="Enter your phone number"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Company/Organization
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-colors text-sm"
                        placeholder="Enter your organization"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Service Interest
                      </label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition-colors text-sm pr-8"
                      >
                        <option value="">Select a service</option>
                        {services.map((service, index) => (
                          <option key={index} value={service}>{service}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Security Clearance Level
                      </label>
                      <select
                        name="clearanceLevel"
                        value={formData.clearanceLevel}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition-colors text-sm pr-8"
                      >
                        <option value="">Select clearance level</option>
                        {clearanceLevels.map((level, index) => (
                          <option key={index} value={level}>{level}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Request Urgency
                    </label>
                    <div className="grid grid-cols-3 gap-4">
                      {['standard', 'urgent', 'emergency'].map((level) => (
                        <label key={level} className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="urgency"
                            value={level}
                            checked={formData.urgency === level}
                            onChange={handleInputChange}
                            className="sr-only"
                          />
                          <div className={`w-full py-2 px-4 rounded-lg text-center text-sm font-medium transition-all ${
                            formData.urgency === level
                              ? 'bg-yellow-500 text-black'
                              : 'bg-gray-700/50 text-gray-300 hover:bg-gray-600/50'
                          }`}>
                            {level.charAt(0).toUpperCase() + level.slice(1)}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-colors text-sm resize-none"
                      placeholder="Describe your security requirements (max 500 characters)"
                    />
                    <div className="text-right text-xs text-gray-400 mt-1">
                      {formData.message.length}/500 characters
                    </div>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="bg-green-500/20 border border-green-500 rounded-lg p-4">
                      <p className="text-green-400 text-sm">
                        Message sent successfully. Our team will contact you within 24 hours for standard requests.
                      </p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-500/20 border border-red-500 rounded-lg p-4">
                      <p className="text-red-400 text-sm">
                        Failed to send message. Please try again or contact us directly.
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 disabled:from-gray-600 disabled:to-gray-700 text-black font-bold py-4 px-8 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Sending Secure Message...' : 'Send Secure Message'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
